import React from 'react';
import { Check, ChevronRight, Fuel } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { cn } from '../lib/utils';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';

export default function Pricing() {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Regular',
      price: '$100',
      period: 'per report',
      description: 'Free to sign up. Purchase individual valuation credits as you need them.',
      features: [
        'Full valuation report (see details below)',
        'Analytics dashboard access',
        'Monthly market updates & newsletter',
      ],
      buttonText: 'Get Started Free',
      buttonAction: () => navigate('/valuation'),
      highlight: false,
      footnote: null,
      // White — clean, entry-level
      accentColor: 'bg-gray-300',
      borderColor: 'border-gray-300',
      badgeColor: 'bg-gray-100 text-gray-700',
      checkColor: 'text-gray-500',
      betaBadge: true,
    },
    {
      name: 'Premium',
      price: '$500',
      period: 'per month',
      description: 'For brokers and operators managing multiple stations. Up to 10 valuations included monthly.',
      features: [
        'Everything in Regular, plus:',
        '10 valuation credits per month included',
        'Discounted additional reports',
        'Multi-station comparison & portfolio tools',
        'Full analytics dashboard with portfolio overview',
        'Side-by-side property benchmarking',
        'Dashboard customization for your use case',
        'Priority support',
      ],
      buttonText: 'Subscribe to Premium',
      buttonAction: () => navigate('/contact-us'),
      highlight: true,
      footnote: 'Premium subscribers can request dashboard customization tailored to their specific workflow — whether you focus on acquisitions, portfolio management, or brokerage.',
      // Red — like the premium octane button
      accentColor: 'bg-red-600',
      borderColor: 'border-red-400',
      badgeColor: 'bg-red-100 text-red-800',
      checkColor: 'text-red-600',
      betaBadge: true,
    },
    {
      name: 'Diesel (Enterprise)',
      price: 'Custom',
      period: '',
      description: 'For teams and organizations running more than 10 reports per month. Custom pricing, dedicated support, and tailored integrations.',
      features: [
        'Everything in Premium, plus:',
        'Unlimited valuation credits',
        'Custom dashboard configuration for your team',
        'Dedicated account manager',
        'Custom data integrations & API access',
        'White-label reporting options',
      ],
      buttonText: 'Contact Us',
      buttonAction: () => navigate('/contact-us'),
      highlight: false,
      footnote: 'Enterprise clients receive a fully customized dashboard experience built around their operational needs — from portfolio-level analytics to deal pipeline tracking.',
      // Green — like the diesel nozzle
      accentColor: 'bg-green-600',
      borderColor: 'border-green-400',
      badgeColor: 'bg-green-100 text-green-800',
      checkColor: 'text-green-600',
      betaBadge: false,
    },
  ];

  return (
    <>
      <SEO
        title="Pricing — Gas Station Valuation Reports | The Fuel Central"
        description="Professional gas station valuation reports starting at $100. Premium plans for brokers and multi-station operators at $500/month with 10 reports included. Beta pricing available now."
        keywords="gas station valuation cost, gas station valuation pricing, fuel station appraisal cost, gas station market value report"
      />
      <SchemaMarkup type="Service" />

      <div className="w-full">
        {/* Hero */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Simple, Transparent Pricing</h1>
              <p className="text-xl text-blue-100">
                Institutional-grade gas station valuations — pay per report or subscribe for volume.
              </p>
            </div>
          </div>
        </div>

        {/* Launch Pricing Notice */}
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="container mx-auto px-4 py-4">
            <div className="max-w-4xl mx-auto flex items-center justify-center gap-3 text-center">
              <span className="inline-block bg-amber-100 text-amber-800 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                Beta Pricing
              </span>
              <p className="text-sm text-amber-800">
                You're getting in early. These are beta rates while we continue to expand coverage and features. Currently serving Orange, Riverside, and San Bernardino Counties with more on the way. Pricing will increase as the platform matures — lock in your rate by subscribing now.
              </p>
            </div>
          </div>
        </div>

        {/* Plans */}
        <div className="container mx-auto px-4 py-16">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => (
              <Card
                key={plan.name}
                className={cn(
                  'p-0 relative overflow-hidden',
                  plan.highlight && `border-2 ${plan.borderColor}`
                )}
              >
                {/* Dispenser-style color bar at top */}
                <div className={cn('h-3', plan.accentColor)} />

                <div className="flex flex-col h-full p-8">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold">{plan.name}</h2>
                      {plan.betaBadge && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300">
                          Beta
                        </span>
                      )}
                    </div>
                    {plan.highlight && (
                      <span className={cn(
                        'text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider',
                        plan.badgeColor
                      )}>
                        Best Value
                      </span>
                    )}
                  </div>
                  <div className="mb-4">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    {plan.period && <span className="text-gray-500 ml-2">{plan.period}</span>}
                  </div>
                  <p className="text-gray-600 mb-6">{plan.description}</p>

                  <div className="flex-grow">
                    <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-gray-500">
                      What's included
                    </h3>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start">
                          <Check className={cn('w-5 h-5 mr-2 flex-shrink-0 mt-0.5', plan.checkColor)} />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {plan.footnote && (
                    <p className="text-xs text-gray-500 italic mb-4 border-t border-gray-100 pt-4">
                      {plan.footnote}
                    </p>
                  )}

                  <Button
                    variant="primary"
                    onClick={plan.buttonAction}
                    className="w-full justify-center"
                  >
                    {plan.buttonText}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {/* Value Framing Callout */}
          <div className="max-w-4xl mx-auto mt-12">
            <div className="bg-gray-900 text-white rounded-xl p-8 flex items-center gap-6">
              <Fuel className="w-10 h-10 text-amber-400 flex-shrink-0" />
              <div>
                <p className="text-lg font-semibold mb-1">
                  The average gas station transaction in California has exceeded $3.8 million since 2024.
                </p>
                <p className="text-gray-400 text-sm">
                  Know what you're buying before you buy. Know what you're selling before you sell.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ / Bottom CTA */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4">Questions?</h3>
            <p className="text-gray-600 mb-6">
              We're happy to walk you through the platform or help you figure out which plan makes sense for your needs.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/contact-us')}
            >
              Contact Us
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
